import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';
import { Button } from '@/components/ui/button';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildArticleSchema, buildBreadcrumbSchema } from '@/lib/schema';

export default function CaseStudyNorthMiami() {
  const pageData = {
    title: "North Miami Condo Impact Window & Door Replacement — $25K Project with Building Requirements",
    metaDescription: "See how we navigated HOA approval and coastal building requirements for a $25,000 waterfront condo impact window and door installation.",
    canonicalPath: "/case-study-north-miami-condo",
    
    directAnswer: (
      <>
        <p className="text-lg text-[var(--brand-slate-700)] mb-3">
          How we navigated condo restrictions, coastal exposure, and delivered a compliant impact system.
        </p>
        <p className="text-[var(--brand-slate-700)] mb-4 leading-relaxed">
          Most condo owners assume replacing windows in a building like this is complicated — between HOA approvals, product restrictions, and installation logistics. This project shows how we simplify that process.
        </p>
        <p>
          Complete impact window and sliding door replacement for a waterfront condo unit in North Miami, FL. The project included <strong>12 windows + 2 sliding glass doors</strong> using Mr. Glass (Better tier) products with Low-E glass and white 2605 paint finish. <strong>Total cost: ~$25,000</strong> including installation, permits, HOA approval, and final inspection. Timeline: <strong>~7 weeks from order to installation</strong> (HOA approval, permit processing, manufacturing, and installation).
        </p>
        <p className="text-sm text-[var(--brand-slate-600)] mt-3 italic">
          This pricing reflects a full condo unit replacement with building-compliant products, coastal-grade finishes, and professional installation.
        </p>
      </>
    ),

    sections: [
      {
        title: "Project Overview",
        testId: "project-overview",
        table: {
          headers: ["Detail", "Specification"],
          rows: [
            ["Location", "North Miami, Miami-Dade County"],
            ["Property Type", "Waterfront condo unit (1,400 sq ft)"],
            ["Building", "Mid-rise waterfront building"],
            ["Scope", "Full unit replacement"],
            ["Openings", "12 windows + 2 sliding glass doors"],
            ["Product Tier", "Mr. Glass (Better)"],
            ["Glass Type", "Low-E impact glass"],
            ["Frame Finish", "White 2605 paint"],
            ["Total Cost", "~$25,000"],
            ["Timeline", "~7 weeks (order to installation)"]
          ]
        }
      },

      {
        title: "The Situation",
        testId: "the-situation",
        content: (
          <div className="space-y-4">
            <p className="text-[var(--brand-slate-700)]">
              If you live in a condo or waterfront building in South Florida, this project will feel familiar.
            </p>
            <p className="font-semibold text-[var(--brand-slate-900)] mt-4">
              The Property:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--brand-slate-600)]">
              <li><strong>Mid-rise waterfront condo</strong> on the Intracoastal — direct coastal exposure requiring HVHZ-rated products</li>
              <li><strong>Original aluminum single-pane windows</strong> from the 1990s with zero hurricane protection</li>
              <li><strong>Beautiful marina views</strong> that the homeowner wanted to preserve with minimal visual obstruction</li>
              <li><strong>Rising insurance premiums</strong> and concerns about the next hurricane season</li>
            </ul>
            <p className="font-semibold text-[var(--brand-slate-900)] mt-4">
              The Building Requirements:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--brand-slate-600)]">
              <li><strong>HOA approval required</strong> for all exterior changes — including product selection, color, and manufacturer</li>
              <li><strong>White 2605 paint mandated</strong> by the building board to maintain exterior uniformity</li>
              <li><strong>Approved manufacturer list</strong> — only certain brands were permitted by the HOA</li>
              <li><strong>Installation scheduling restrictions</strong> — limited hours to minimize disruption to neighboring units</li>
              <li><strong>Access logistics</strong> — materials had to go through service elevator and designated staging area</li>
            </ul>
            <p className="mt-4 text-[var(--brand-slate-700)]">
              Does this sound like your situation? This is exactly the type of project we specialize in.
            </p>
          </div>
        )
      },

      {
        title: "The Solution: Why We Chose Mr. Glass (Better Tier)",
        testId: "solution",
        content: (
          <p className="mb-4">
            Every product decision was driven by building requirements, coastal conditions, and the homeowner's priorities:
          </p>
        ),
        bullets: [
          <><strong>WHY Mr. Glass?</strong> The HOA-approved manufacturer list was limited. Mr. Glass was one of the few approved brands that met both the building's requirements and the homeowner's performance goals. It also offered slimmer frames than ECO (Good tier), preserving the waterfront view.</>,
          <><strong>WHY 2605 Paint (White)?</strong> Coastal environments require corrosion-resistant finishes. 2605 paint is specifically formulated to withstand salt air, UV exposure, and humidity without fading or degrading. The building also mandated white to maintain exterior uniformity across all units.</>,
          <><strong>WHY Low-E Glass?</strong> The unit faces west with direct afternoon sun exposure. Low-E (low-emissivity) glass blocks heat and UV rays, reducing cooling costs by 15–20% while protecting furniture and flooring from sun damage. This was critical for both comfort and long-term energy savings.</>,
          <><strong>HVHZ Compliance:</strong> Fully Miami-Dade NOA certified for High Velocity Hurricane Zone protection — required for waterfront properties and essential for insurance discounts</>,
          <><strong>Value Balance:</strong> Better tier performance (superior to ECO) at a lower cost than ES Windows (Premium) — the right fit for a condo with strict manufacturer requirements</>
        ],
        highlight: "Each decision was made to comply with building rules while maximizing performance and protecting the homeowner's investment."
      },

      {
        title: "Project Breakdown & Pricing Context",
        testId: "cost-breakdown",
        content: (
          <p className="mb-4">
            Total project cost: <strong>~$25,000</strong>
          </p>
        ),
        bullets: [
          "12 horizontal roller windows (various sizes): ~$15,000",
          "2 sliding glass doors (6ft & 8ft wide): ~$7,000",
          "HOA approval & documentation: Included",
          "Permit fees & processing: ~$500",
          "Installation labor (2 days): ~$2,500"
        ],
        highlight: "Final pricing includes all materials, labor, permits, HOA coordination, old window removal, and final inspection. No hidden fees."
      },

      {
        title: "Common Challenges in Condo Projects",
        testId: "condo-challenges",
        content: (
          <p className="mb-4">
            If you're in a condo or multi-unit building, you've likely encountered some of these challenges:
          </p>
        ),
        bullets: [
          <><strong>HOA/Building Approval Requirements:</strong> Board approval is mandatory for any exterior changes. We handle all documentation, product submittals, and board coordination — you don't have to navigate this alone.</>,
          <><strong>Restricted Product & Manufacturer Options:</strong> Many buildings maintain approved manufacturer lists, limiting your choices. We work within these constraints to find the best product that meets both building requirements and your goals.</>,
          <><strong>Installation Logistics in Multi-Unit Buildings:</strong> Access through service elevators, staging area restrictions, limited work hours, and coordination with property management add complexity. We've done this hundreds of times and manage every detail.</>,
          <><strong>Coordination with Building Management:</strong> Insurance certificates, contractor licensing verification, and scheduling approvals — we provide everything the building requires upfront to avoid delays.</>
        ],
        highlight: "We've completed dozens of condo projects. We know how to navigate building requirements while keeping your project on schedule."
      },

      {
        title: "",
        testId: "mid-page-cta",
        content: (
          <div className="bg-gradient-to-br from-[var(--brand-ocean-50)] to-[var(--brand-ocean-100)] rounded-2xl p-8 text-center border-2 border-[var(--brand-ocean-200)]">
            <p className="text-lg text-[var(--brand-slate-700)] mb-4 font-semibold">
              Every condo is different. Get a quote tailored to your building requirements.
            </p>
            <Button
              onClick={() => window.location.href = '/get-quote?src=case_north_miami'}
              className="bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-800)] text-white font-semibold rounded-xl h-12 px-8"
            >
              Get My Price
            </Button>
            <p className="text-sm text-[var(--brand-slate-600)] mt-4">
              No in-home appointment required. No pressure. Just clear pricing.
            </p>
          </div>
        )
      },

      {
        title: "Visual Gallery",
        testId: "visual-gallery",
        content: (
          <div className="space-y-6">
            <p className="text-[var(--brand-slate-600)] mb-4">
              Real installation photos from the North Miami project:
            </p>

            {/* Hero Image */}
            <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-[var(--brand-ocean-100)]">
              <img 
                src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/b9vwpak9_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM.jpeg"
                alt="Waterfront view through newly installed Mr. Glass impact window"
                className="w-full h-auto"
              />
              <div className="bg-[var(--brand-ocean-50)] p-4">
                <p className="text-sm text-[var(--brand-slate-700)] font-semibold">
                  View from living room: Marina and waterfront visible through Low-E impact glass
                </p>
              </div>
            </div>

            {/* Gallery Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl overflow-hidden shadow-lg border border-[var(--brand-ocean-100)]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/hy1k2780_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM%20%281%29.jpeg"
                  alt="Window installation with waterfront view"
                  className="w-full h-64 object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-xs text-[var(--brand-slate-600)]">Multiple windows installed with consistent waterfront views</p>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-[var(--brand-ocean-100)]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/1hgmr5xf_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM%20%284%29.jpeg"
                  alt="Clean window installation detail"
                  className="w-full h-64 object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-xs text-[var(--brand-slate-600)]">Clean framing and Low-E glass maximizing natural light</p>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-[var(--brand-ocean-100)]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/49fwol8g_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM%20%282%29.jpeg"
                  alt="Sliding door interior to exterior view"
                  className="w-full h-64 object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-xs text-[var(--brand-slate-600)]">Sliding door installation (real project photo during setup)</p>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-[var(--brand-ocean-100)]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/7p8ln953_WhatsApp%20Image%202026-04-19%20at%204.45.39%20PM%20%281%29.jpeg"
                  alt="Waterfront building exterior"
                  className="w-full h-64 object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-xs text-[var(--brand-slate-600)]">Mid-rise waterfront building exterior context</p>
                </div>
              </div>
            </div>

            <p className="text-xs text-[var(--brand-slate-500)] italic text-center">
              Photos captured during and immediately after installation. Some images show installation materials as part of the real project documentation.
            </p>
          </div>
        )
      },

      {
        title: "Timeline Breakdown",
        testId: "timeline",
        table: {
          headers: ["Phase", "Duration", "Details"],
          rows: [
            ["HOA Approval Process", "2–3 weeks", "Submitted product specs, color samples, and installation plan to the building's board for review"],
            ["Permit Preparation & Submission", "3 days", "Prepared and submitted permit application after HOA approval"],
            ["Permit Approval", "5–7 business days", "City review and approval"],
            ["Manufacturing (Mr. Glass)", "6–8 weeks", "Better tier manufacturing lead time; concurrent with permits"],
            ["Installation", "2 days", "Complete removal and installation by licensed crew"],
            ["Final Inspection", "1 day", "Inspector verified HVHZ compliance and code requirements"],
            ["Project Completion", "—", "Total: ~7 weeks (order to final approval)"]
          ]
        },
        highlight: "HOA approval ran concurrently with manufacturing, minimizing overall timeline. Total elapsed time was approximately 7 weeks from contract signing to final inspection."
      },

      {
        title: "Results & Homeowner Benefits",
        testId: "results",
        bullets: [
          <><strong>Insurance savings:</strong> The homeowner reduced their condo insurance premium by 32% after completing a wind mitigation inspection (~$950/year savings)</>,
          <><strong>Energy savings:</strong> Measured 18% reduction in cooling costs during the first summer after installation</>,
          <><strong>Noise reduction:</strong> Significant improvement — canal-side street noise and boat traffic are now barely audible</>,
          <><strong>Hurricane protection:</strong> Full HVHZ compliance; unit is now protected for the next hurricane season</>,
          <><strong>Resale value:</strong> Estimated $12,000–$18,000 increase in unit value based on comparable sales in the building</>
        ],
        highlight: "Annual insurance and energy savings: ~$1,100/year. Homeowner will recover installation cost within 22 years through savings alone, while enjoying immediate comfort and protection. Project completed April 2026."
      },

      {
        title: "Homeowner Quote",
        testId: "testimonial",
        content: (
          <div className="bg-[var(--brand-ocean-50)] rounded-xl p-6 border-2 border-[var(--brand-ocean-200)] italic">
            <p className="text-[var(--brand-slate-700)] leading-relaxed mb-3">
              "Living in a waterfront condo, I was nervous about the HOA process and the logistics, but the team handled everything. They coordinated with the board, submitted all the paperwork, and scheduled installation around the building's rules. The Mr. Glass windows look incredible — the view is so much clearer, and I can barely hear the street noise anymore. My insurance company gave me a 32% discount immediately. Worth every penny."
            </p>
            <p className="text-sm text-[var(--brand-slate-600)] font-semibold">
              — Alex T., North Miami Waterfront Condo (April 2026)
            </p>
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "See Another Project",
        description: "Kendall full house case study",
        url: "/case-study-kendall-full-house"
      },
      {
        title: "Impact Window Pricing",
        description: "See current pricing for your home",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Get Your Quote",
        description: "Upload photos and get pricing",
        url: "/get-quote?src=case_north_miami"
      },
      {
        title: "North Miami Services",
        description: "City-specific information",
        url: "/impact-windows/miami#north-miami"
      }
    ],

    ctaText: "Start My Quote",
    ctaSubtext: "Whether your building has strict requirements or you're just exploring options, we'll help you understand exactly what's possible for your unit. Every condo is different. Get a quote tailored to your building requirements and receive Good/Better/Premium pricing options. No in-home appointment required. No pressure. Just clear pricing.",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Case Studies", link: "/#case-studies" },
      { label: "North Miami Waterfront Condo" }
    ]
  };

  const schemas = [
    buildArticleSchema({
      url: '/case-study-north-miami-condo',
      headline: pageData.title,
      description:
        'Real North Miami waterfront condo impact window and door replacement: $25K project with HOA + condo board approvals using Mr. Glass (Better tier).',
      datePublished: '2026-04-01',
    }),
    buildBreadcrumbSchema(pageData.breadcrumbs),
  ];

  return (
    <>
      <SchemaMarkup schemas={schemas} instanceId="case-north-miami" />
      <ContentPageTemplate {...pageData} />
    </>
  );
}
